import {
  ConstructGeneralViewStateParam,
  OpportunityReviewRestoreLocalSavedViewProps,
  OpportunityReviewViewState,
} from '@models/SavedViews';
import { useDispatch, useSelector } from 'react-redux';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { bindActionCreators } from 'redux';
import { actionCreators, actionCreatorsSideFilters } from '@state/Index';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { useRestoreSavedView } from '@ecosystem/newron-client-lib';
import { useRestoreMultibannerRetailerClient } from './UseRestoreMultibannerRetailerClient';
import { useRestoreProductMultibannerRetailerClient } from './UseRestoreProductMultibannerRetailerClient';
import {
  checkIsSubscribed,
  constructGeneralViewStateProps,
  getGrowthType,
  handleCustomPeriodicityAndRestoreGeneralViewState,
  handleRestoreMultibannerChange,
} from './SavedViewHookUtils';

/**
 *  React custom hooks to restore the saved views for Performance Analysis page
 *  @param oRRestoreLocalSavedViewProps contains local setState from PA page
 */
export const useRestoreOpportunityReviewSavedView = (
  oRRestoreLocalSavedViewProps: OpportunityReviewRestoreLocalSavedViewProps,
): void => {
  const {
    productType,
    setIsSubscribed,
    setFilters,
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters,
    setGrowthOption,
    setIsTableView,
    setBubbleSliderCurrentPeriod,
    setSaleType,
    setRetailerType,
    setFeature,
    setCustomMonthsPeriodState,
    setCustomWeeksPeriodState,
    setRelativePeriod,
    setTimeFrame,
    setPeriod,
  } = oRRestoreLocalSavedViewProps;

  // generate object holding the general states to be passed in the function later on
  const generalSavedViewProps = constructGeneralViewStateProps(oRRestoreLocalSavedViewProps);

  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);

  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const mainFiltersState = useSelector((state: any) => state.mainFilters);

  const { restoreMultibannerRetailerClient } = useRestoreMultibannerRetailerClient();
  const { restoreProductMultibannerRetailerClient } = useRestoreProductMultibannerRetailerClient();

  const pageName = PageName.OPPORTUNITY_REVIEW;
  const pageType = PageType.OPPORTUNITY_REVIEW;

  const applyORViewState = (viewState: OpportunityReviewViewState): void => {
    // initial checks
    if (!checkIsSubscribed(viewState.retailerClientState, pagesConfig, productType)) {
      setIsSubscribed(false);
      return;
    }

    if (!viewState) {
      return;
    }

    const params: ConstructGeneralViewStateParam = {
      viewState,
      pagesConfig,
      pageType,
      pageName,
      updateSideFilters,
      updateMainFilters,
      mainFiltersState,
      generalSavedViewProps,
    };

    // restore the general states that exist in all pages
    handleCustomPeriodicityAndRestoreGeneralViewState(
      viewState,
      params,
      setCustomMonthsPeriodState,
      setCustomWeeksPeriodState,
    );

    // below are states that are specific to OR page
    setIsTableView(viewState.isTableView);

    if (viewState.selectedRetailerType) {
      setRetailerType(viewState.selectedRetailerType);
    }

    if (viewState.selectedSaleType) {
      setSaleType(viewState.selectedSaleType);
    }

    if (viewState.mainFilter) {
      setRelativePeriod(viewState.mainFilter.comparisonPeriod!);
      setTimeFrame(viewState.mainFilter.timeFrame!);
      setPeriod(viewState.mainFilter.period!);
    }

    if (viewState.selectedFeature) {
      setFeature(viewState.selectedFeature);
    }

    if (viewState.bubbleSliderCurrentPeriod) {
      setBubbleSliderCurrentPeriod(viewState.bubbleSliderCurrentPeriod);
    }

    // Restore growth type
    if (viewState.growthType) {
      setGrowthOption(getGrowthType(viewState.growthType));
    }

    handleRestoreMultibannerChange({
      viewState,
      retailerClientState,
      pageName,
      pageType,
      pagesConfig,
      restoreMultibannerRetailerClient,
      restoreProductMultibannerRetailerClient,
      setFilters,
      setAppliedCountries,
      setSelectedCountries,
      setDefaultFilters,
    });
  };
  useRestoreSavedView(applyORViewState);
};
